import { ContentLoading } from "../components/ContentLoading";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus, Search, X } from "lucide-react";
import { useState, type Dispatch, type SetStateAction, type SyntheticEvent } from "react";

import type {
  OpsCustomerCreate,
  OpsCustomerPageSize,
  OpsCustomerSummary,
} from "../../shared/operations-schemas";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { createCustomer, listCustomers } from "./api";
import { OperationsLink } from "./OperationsLink";
import { PeopleSearch } from "./PeopleSearch";
import type { OperationsNavigate } from "./navigation";

const PAGE_SIZES: OpsCustomerPageSize[] = [10, 25, 50];
type CustomerForm = OpsCustomerCreate & { firstAdmin: { displayName: string; email: string } };
type CustomerFormSetter = Dispatch<SetStateAction<CustomerForm>>;

function CustomerIdentityFields({
  form,
  setForm,
}: {
  form: CustomerForm;
  setForm: CustomerFormSetter;
}) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="customer-name">Customer name</FieldLabel>
        <Input
          id="customer-name"
          autoComplete="organization"
          value={form.name}
          onChange={(event) => {
            setForm((current) => ({ ...current, name: event.target.value }));
          }}
          required
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="customer-subdomain">Private DAS address</FieldLabel>
        <Input
          id="customer-subdomain"
          value={form.subdomain}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              subdomain: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
            }));
          }}
          required
        />
        <FieldDescription>{form.subdomain || "customer"}.das.dev</FieldDescription>
      </Field>
    </>
  );
}

function FirstAdminFields({ form, setForm }: { form: CustomerForm; setForm: CustomerFormSetter }) {
  return (
    <>
      <Field>
        <FieldLabel htmlFor="admin-name">First administrator name (optional)</FieldLabel>
        <Input
          id="admin-name"
          autoComplete="name"
          value={form.firstAdmin.displayName}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              firstAdmin: { ...current.firstAdmin, displayName: event.target.value },
            }));
          }}
          required={Boolean(form.firstAdmin.email)}
        />
      </Field>
      <Field>
        <FieldLabel htmlFor="admin-email">First administrator email (optional)</FieldLabel>
        <Input
          id="admin-email"
          type="email"
          autoComplete="email"
          value={form.firstAdmin.email}
          onChange={(event) => {
            setForm((current) => ({
              ...current,
              firstAdmin: { ...current.firstAdmin, email: event.target.value },
            }));
          }}
          required={Boolean(form.firstAdmin.displayName)}
        />
        <FieldDescription>
          Leave both blank to set up access later. An invited administrator joins when they sign in
          with Google.
        </FieldDescription>
      </Field>
    </>
  );
}

function CreateCustomerDialog({ navigate }: { navigate: OperationsNavigate }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    subdomain: "",
    firstAdmin: { displayName: "", email: "" },
  });
  const mutation = useMutation({
    mutationFn: createCustomer,
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "customers"] });
      setOpen(false);
      navigate(`/customers/${result.customer.slug}`);
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button type="button" />}>
        <Plus /> New customer
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create customer</DialogTitle>
          <DialogDescription>
            Create the private workspace. You can invite its first administrator now or set up
            access later.
          </DialogDescription>
        </DialogHeader>
        <DialogForm
          aria-label="Create customer"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate({
              name: form.name,
              subdomain: form.subdomain,
              ...(form.firstAdmin.displayName.trim() || form.firstAdmin.email.trim()
                ? { firstAdmin: form.firstAdmin }
                : {}),
            });
          }}
        >
          <DialogBody>
            <FieldGroup>
              <CustomerIdentityFields form={form} setForm={setForm} />
              <FirstAdminFields form={form} setForm={setForm} />
              {mutation.error ? <FieldError>{mutation.error.message}</FieldError> : null}
            </FieldGroup>
          </DialogBody>
          <DialogFooter>
            <DialogClose render={<Button type="button" variant="ghost" />}>Cancel</DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating…" : "Create customer"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function AttentionBadge({ customer }: { customer: OpsCustomerSummary }) {
  if (customer.provisioningStatus === "ready") return null;
  const failed = ["failed", "degraded"].includes(customer.provisioningStatus);
  return (
    <Badge variant={failed ? "destructive" : "secondary"}>
      {failed ? "Needs attention" : "Setting up"}
    </Badge>
  );
}

function CustomerRows({
  customers,
  navigate,
}: {
  customers: OpsCustomerSummary[];
  navigate: OperationsNavigate;
}) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-card">
      <div className="hidden grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_140px] gap-4 border-b bg-muted/40 px-5 py-3 text-xs font-semibold text-muted-foreground md:grid">
        <span>Customer</span>
        <span>Workspace</span>
        <span>Added</span>
      </div>
      {customers.map((customer) => (
        <OperationsLink
          className="grid gap-2 border-b px-5 py-4 no-underline transition-colors last:border-b-0 hover:bg-muted/50 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_140px] md:items-center md:gap-4"
          href={`/customers/${customer.slug}`}
          key={customer.id}
          navigate={navigate}
        >
          <span className="flex min-w-0 items-center gap-3">
            <span className="truncate font-semibold text-foreground">{customer.name}</span>
            <AttentionBadge customer={customer} />
          </span>
          <span className="truncate text-sm text-muted-foreground">
            {customer.subdomain}.das.dev
          </span>
          <time className="text-sm text-muted-foreground tabular-nums">
            {new Date(customer.createdAt * 1_000).toLocaleDateString()}
          </time>
        </OperationsLink>
      ))}
    </div>
  );
}

function CustomerSearchForm({
  searchInput,
  onInputChange,
  onSearch,
  onClear,
}: {
  searchInput: string;
  onInputChange: (value: string) => void;
  onSearch: (event: SyntheticEvent<HTMLFormElement>) => void;
  onClear: () => void;
}) {
  return (
    <form
      className="mt-5 rounded-lg border bg-card p-4 shadow-card"
      role="search"
      onSubmit={onSearch}
    >
      <Label htmlFor="customer-search">Find customers</Label>
      <div className="mt-2 flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="customer-search"
            className="pr-10 pl-9"
            maxLength={100}
            placeholder="Name or workspace address"
            type="search"
            value={searchInput}
            onChange={(event) => {
              onInputChange(event.currentTarget.value);
            }}
          />
          {searchInput ? (
            <Button
              aria-label="Clear customer search"
              className="absolute top-1/2 right-1 size-8 -translate-y-1/2"
              size="icon-sm"
              type="button"
              variant="ghost"
              onClick={onClear}
            >
              <X />
            </Button>
          ) : null}
        </div>
        <Button type="submit">Search</Button>
      </div>
    </form>
  );
}

function CustomerResultsToolbar({
  first,
  last,
  loading,
  pageSize,
  query,
  total,
  onPageSizeChange,
}: {
  first: number;
  last: number;
  loading: boolean;
  pageSize: OpsCustomerPageSize;
  query: string;
  total: number;
  onPageSizeChange: (pageSize: OpsCustomerPageSize) => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <p aria-live="polite" className="text-sm text-muted-foreground tabular-nums">
        {loading
          ? "Loading customers…"
          : `Showing ${first}–${last} of ${total}${query ? ` for “${query}”` : ""}`}
      </p>
      <div className="flex items-center gap-2">
        <Label htmlFor="customer-page-size">Rows per page</Label>
        <select
          id="customer-page-size"
          className="min-h-10 rounded-sm border border-input bg-card px-3 text-sm outline-none focus-visible:border-primary focus-visible:shadow-[var(--field-focus-shadow)]"
          value={pageSize}
          onChange={(event) => {
            onPageSizeChange(Number(event.currentTarget.value) as OpsCustomerPageSize);
          }}
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function CustomerResults({
  fetching,
  loading,
  navigate,
  query,
  rows,
}: {
  fetching: boolean;
  loading: boolean;
  navigate: OperationsNavigate;
  query: string;
  rows: OpsCustomerSummary[];
}) {
  let content = <CustomerRows customers={rows} navigate={navigate} />;
  if (rows.length === 0) {
    content = (
      <p className="rounded-sm border border-dashed p-5 text-sm text-muted-foreground">
        {query ? "No customers match your search." : "No customers have been created yet."}
      </p>
    );
  }
  return (
    <div aria-busy={fetching} className="mt-3">
      {loading ? <ContentLoading label="Loading customer list…" /> : content}
    </div>
  );
}

function CustomerPagination({
  fetching,
  page,
  total,
  totalPages,
  onPageChange,
}: {
  fetching: boolean;
  page: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  if (total === 0) return null;
  return (
    <nav aria-label="Customer pagination" className="mt-5 flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground tabular-nums">
        Page {page} of {totalPages}
      </p>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={page <= 1 || fetching}
          onClick={() => {
            onPageChange(Math.max(1, page - 1));
          }}
        >
          <ChevronLeft /> Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={page >= totalPages || fetching}
          onClick={() => {
            onPageChange(page + 1);
          }}
        >
          Next <ChevronRight />
        </Button>
      </div>
    </nav>
  );
}

function CustomerIndexHeader({
  canCreate,
  navigate,
}: {
  canCreate: boolean;
  navigate: OperationsNavigate;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-balance text-2xl font-bold">Customers</h1>
        <p className="mt-1 text-pretty text-sm text-muted-foreground">
          Find a customer, open its workspace record, or provision a new one.
        </p>
      </div>
      {canCreate ? <CreateCustomerDialog navigate={navigate} /> : null}
    </div>
  );
}

type SearchScope = "customers" | "people";

function SearchScopeTabs({
  onChange,
  scope,
}: {
  onChange: (scope: SearchScope) => void;
  scope: SearchScope;
}) {
  const items: { key: SearchScope; label: string }[] = [
    { key: "customers", label: "Customers" },
    { key: "people", label: "People" },
  ];
  return (
    <nav aria-label="Search scope" className="mt-4 flex gap-1 border-b">
      {items.map((item) => (
        <button
          aria-current={scope === item.key ? "page" : undefined}
          className={`min-h-11 shrink-0 border-b-2 px-4 py-3 text-sm font-semibold ${
            scope === item.key
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          key={item.key}
          onClick={() => {
            onChange(item.key);
          }}
          type="button"
        >
          {item.label}
        </button>
      ))}
    </nav>
  );
}

/**
 * The paginated customer directory. Extracted from `CustomerIndex` so the People scope can replace
 * it wholesale rather than the index owning both surfaces' state in one component.
 */
function CustomerDirectory({ navigate }: { navigate: OperationsNavigate }) {
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<OpsCustomerPageSize>(25);
  const customers = useQuery({
    queryKey: ["ops", "customers", { query, page, pageSize }],
    queryFn: () => listCustomers({ ...(query ? { query } : {}), page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const rows = customers.data?.customers ?? [];
  const total = customers.data?.total ?? 0;
  const first = rows.length ? (page - 1) * pageSize + 1 : 0;
  const last = rows.length ? first + rows.length - 1 : 0;

  function search(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    setQuery(searchInput.trim());
    setPage(1);
  }

  function clearSearch() {
    setSearchInput("");
    setQuery("");
    setPage(1);
  }

  function changePageSize(nextPageSize: OpsCustomerPageSize) {
    setPageSize(nextPageSize);
    setPage(1);
  }

  return (
    <>
      <CustomerSearchForm
        searchInput={searchInput}
        onClear={clearSearch}
        onInputChange={setSearchInput}
        onSearch={search}
      />
      {customers.error ? (
        <div className="mt-4">
          <FieldError>{customers.error.message}</FieldError>
        </div>
      ) : null}
      <CustomerResultsToolbar
        first={first}
        last={last}
        loading={customers.isPending}
        onPageSizeChange={changePageSize}
        pageSize={pageSize}
        query={query}
        total={total}
      />
      <CustomerResults
        fetching={customers.isFetching}
        loading={customers.isPending}
        navigate={navigate}
        query={query}
        rows={rows}
      />
      <CustomerPagination
        fetching={customers.isFetching}
        onPageChange={setPage}
        page={page}
        total={total}
        totalPages={customers.data?.totalPages ?? 1}
      />
    </>
  );
}

export function CustomerIndex({
  canCreate,
  canSearchPeople,
  navigate,
}: {
  canCreate: boolean;
  canSearchPeople: boolean;
  navigate: OperationsNavigate;
}) {
  const [scope, setScope] = useState<SearchScope>("customers");
  const searchingPeople = canSearchPeople && scope === "people";
  return (
    <section>
      <CustomerIndexHeader canCreate={canCreate} navigate={navigate} />
      {canSearchPeople ? <SearchScopeTabs onChange={setScope} scope={scope} /> : null}
      {searchingPeople ? (
        <PeopleSearch navigate={navigate} />
      ) : (
        <CustomerDirectory navigate={navigate} />
      )}
    </section>
  );
}
